/**
 * useKeyboardInset — the on-screen keyboard's height in px (0 when closed).
 *
 * For position:fixed UI such as bottom sheets, which useKeyboardAware can't
 * help: scrolling never moves a fixed element, and `resize: 'body'`
 * (capacitor.config.ts) shrinks only <body>, not the viewport a fixed sheet
 * is anchored to — so a bottom-anchored sheet stays behind the keyboard.
 * Callers lift themselves by this inset.
 *
 * Native: the @capacitor/keyboard window events (keyboardWillShow carries
 * keyboardHeight). Web: how far the visual viewport has shrunk.
 *
 *   const kb = useKeyboardInset();
 */
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

export default function useKeyboardInset() {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const onShow = (event) => setInset(Math.max(0, Math.round((event && event.keyboardHeight) || 0)));
      const onHide = () => setInset(0);
      window.addEventListener('keyboardWillShow', onShow);
      window.addEventListener('keyboardWillHide', onHide);
      return () => {
        window.removeEventListener('keyboardWillShow', onShow);
        window.removeEventListener('keyboardWillHide', onHide);
      };
    }

    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onViewport = () => setInset(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    onViewport();
    vv.addEventListener('resize', onViewport);
    vv.addEventListener('scroll', onViewport);
    return () => {
      vv.removeEventListener('resize', onViewport);
      vv.removeEventListener('scroll', onViewport);
    };
  }, []);

  return inset;
}
